import {
  AccountingEventsGroup,
  CanonicalBusinessEvent,
  DigitalBusinessDocument,
  DigitalCostDocument,
} from "../types";
import {
  isCanonicalBusinessEvent,
  isDigitalBusinessDocument,
  isDigitalDocument,
  isDocumentFlowCbe,
} from "./typeChecks";
import { isNoOpCBE } from "./cbeUtils";

export const FLOW_TYPES = {
  DOCUMENT: "document",
  ACCOUNTING: "accounting",
} as const;

export type FlowType = (typeof FLOW_TYPES)[keyof typeof FLOW_TYPES];

const FLOW_LABELS = {
  DUAL_FLOW: {
    [FLOW_TYPES.DOCUMENT]: "Document",
    [FLOW_TYPES.ACCOUNTING]: "Classic",
  },
  SINGLE_FLOW: {
    [FLOW_TYPES.DOCUMENT]: "Royalty",
    [FLOW_TYPES.ACCOUNTING]: "Revenue",
  },
} as const;

type EventsArray = (
  | AccountingEventsGroup
  | DigitalBusinessDocument
  | DigitalCostDocument
)[];

const hasDocumentEventWithPayment = (
  event:
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | CanonicalBusinessEvent
) => {
  if (isDigitalBusinessDocument(event)) {
    const { payments } = event.body.body;
    return !!payments && Object.entries(payments).length > 0;
  }
  return false;
};

const isRevenueBookedByDocumentFlow = (events: EventsArray): boolean => {
  return events.some(
    (event) =>
      hasDocumentEventWithPayment(event) &&
      isDigitalDocument(event) &&
      !(
        isDigitalBusinessDocument(event) &&
        (event.body.metadata.internalAttributes?.REVENUE_BOOKINGS_DISABLED ||
          false)
      )
  );
};

const isRevenueBookedByAccountingFlow = (
  cbes: CanonicalBusinessEvent[]
): boolean => {
  const accountingCBEs = cbes.filter((cbe) => !isDocumentFlowCbe(cbe));
  return accountingCBEs.some((cbe) => !isNoOpCBE(cbe));
};

export function shouldHideAccountingFlow(
  events: EventsArray,
  cbes: CanonicalBusinessEvent[] = []
): boolean {
  const hasDocumentEvents = events.some((event) => isDigitalDocument(event));
  const revenueBookedByDocument = isRevenueBookedByDocumentFlow(events);
  const revenueBookedByAccounting = isRevenueBookedByAccountingFlow(cbes);

  // Hide accounting flow if:
  // 1. There are document events AND
  // 2. Revenue is booked by document flow AND
  // 3. Revenue is NOT booked by accounting flow
  return (
    hasDocumentEvents && revenueBookedByDocument && !revenueBookedByAccounting
  );
}

export function shouldHideDocumentFlow(events: EventsArray): boolean {
  return !events.some((event) => isDigitalDocument(event));
}

export function isRevenueBookedByBothFlows(
  events: EventsArray,
  cbes: CanonicalBusinessEvent[]
): boolean {
  return (
    isRevenueBookedByDocumentFlow(events) &&
    isRevenueBookedByAccountingFlow(cbes)
  );
}

function getFlowLabel(
  flowType: FlowType,
  events: EventsArray,
  cbes: CanonicalBusinessEvent[],
  labelType: "display" | "selector"
): string {
  const isDualFlow = isRevenueBookedByBothFlows(events, cbes);
  const labelConfig = isDualFlow
    ? FLOW_LABELS.DUAL_FLOW
    : FLOW_LABELS.SINGLE_FLOW;

  const baseLabel = labelConfig[flowType] ?? "Unknown";
  return labelType === "display" ? `${baseLabel} Flow` : baseLabel;
}

export function getFlowDisplayName(
  flowType: FlowType,
  events: EventsArray = [],
  cbes: CanonicalBusinessEvent[] = []
): string {
  return getFlowLabel(flowType, events, cbes, "display");
}

export function getFlowSelectorLabel(
  flowType: FlowType,
  events: EventsArray = [],
  cbes: CanonicalBusinessEvent[] = []
): string {
  return getFlowLabel(flowType, events, cbes, "selector");
}

export function determineFlowTypeFromObjects(
  objects: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | CanonicalBusinessEvent
  )[]
): FlowType {
  if (objects.length === 0) return FLOW_TYPES.DOCUMENT;

  const events = objects.filter(
    (
      obj
    ): obj is
      | AccountingEventsGroup
      | DigitalBusinessDocument
      | DigitalCostDocument => !isCanonicalBusinessEvent(obj)
  );

  const cbes = objects.filter(isCanonicalBusinessEvent);

  const hasDocumentFlow =
    !shouldHideDocumentFlow(events) || cbes.some(isDocumentFlowCbe);
  const hasAccountingFlow =
    events.some((event) => !isDigitalDocument(event)) ||
    cbes.some((cbe) => !isDocumentFlowCbe(cbe));

  const shouldHideAccounting = shouldHideAccountingFlow(events, cbes);

  if (shouldHideAccounting || (hasDocumentFlow && !hasAccountingFlow)) {
    return FLOW_TYPES.DOCUMENT;
  }
  return FLOW_TYPES.ACCOUNTING;
}
